import { navigate, ROUTES, type Route, useRoute } from './routes';
import { useAppStore } from './store';

/**
 * The left navigation (ProjectionLab-style). Top-level screens (Plan / Results / Ledger). The Plan
 * editor's intra-page SECTION nav (household, accounts, …) lives inside the editor screen, not here.
 *
 * Results + Ledger depend on a simulation having run: until there is a `result`, they read as
 * pending (a muted "run first" hint) but stay navigable — each screen shows its own EmptyState that
 * routes back to the plan. The active link carries the accent-faint pill + `aria-current`.
 */

/** Routes that need a prior simulation run to show anything. */
const RUN_GATED: ReadonlySet<Route> = new Set<Route>(['results', 'ledger']);

export function LeftNav(): React.ReactElement {
  const route = useRoute();
  const hasResult = useAppStore((s) => s.result !== null);

  return (
    <nav className="nav" aria-label="Primary">
      <div className="nav__brand">
        Projection
        <small>your financial future, read like a ledger</small>
      </div>
      <div className="nav__group">
        <div className="nav__group-label">Plan</div>
        {ROUTES.map((r) => {
          const pending = RUN_GATED.has(r.id) && !hasResult;
          return (
            <button
              key={r.id}
              type="button"
              className={`nav__link${route === r.id ? ' nav__link--active' : ''}${
                pending ? ' nav__link--pending' : ''
              }`}
              aria-current={route === r.id ? 'page' : undefined}
              onClick={() => navigate(r.id)}
            >
              <span>{r.label}</span>
              {pending && <span className="nav__hint">run first</span>}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
